import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Text from 'antd/lib/typography/Text';
import Button from 'antd/lib/button';
import Space from 'antd/lib/space';
import Modal from 'antd/lib/modal';
import Form from 'antd/lib/form';
import Select from 'antd/lib/select';
import { EditOutlined } from '@ant-design/icons';
import { OrgComponentProps } from '../../typescriptHelpers';
import { RootState } from '../../reducers/reducers';
import { selectOrg } from '../../selectors/selectors';
import { OrgSettingsUpdatable } from '../../actions/updatable';
import { tryUpdateOrgSettings, UpdateOrgCalendarFirstDayOfWeekRequest } from '../../actions/orgSettings';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { globalMessage } from '../../utils/globalMessage';

const FIRST_DAY_OF_WEEK_OPTIONS = [
    { value: 0, label: 'Sunday' },
    { value: 1, label: 'Monday' },
];

const firstDayOfWeekLabel = (firstDayOfWeek: number) =>
    FIRST_DAY_OF_WEEK_OPTIONS.find((option) => option.value === firstDayOfWeek)?.label ??
    FIRST_DAY_OF_WEEK_OPTIONS[0].label;

const OrgCalendarFirstDayOfWeekSetting: React.FC<OrgComponentProps> = (props) => {
    const dispatch = useDispatch();
    const org = useSelector((state: RootState) => selectOrg(state, props.orgId));
    const [modalOpen, setModalOpen] = React.useState(false);
    const [form] = Form.useForm();

    const firstDayOfWeek = org?.calendarFirstDayOfWeek ?? 0;

    const isLoading = useIsLoading(OrgSettingsUpdatable.UpdateOrgCalendarFirstDayOfWeek);
    const error = useError(OrgSettingsUpdatable.UpdateOrgCalendarFirstDayOfWeek);
    const settingUpdated = useUpdated(OrgSettingsUpdatable.UpdateOrgCalendarFirstDayOfWeek);

    React.useEffect(() => {
        if (settingUpdated) {
            globalMessage.success('Release calendar layout updated!');
            setModalOpen(false);
        }
    }, [settingUpdated]);

    React.useEffect(() => {
        if (error) {
            globalMessage.error(error);
        }
    }, [error]);

    const onSave = (values: { calendarFirstDayOfWeek: number }) => {
        const request: UpdateOrgCalendarFirstDayOfWeekRequest = {
            orgId: props.orgId,
            calendarFirstDayOfWeek: values.calendarFirstDayOfWeek,
        };
        dispatch(tryUpdateOrgSettings(OrgSettingsUpdatable.UpdateOrgCalendarFirstDayOfWeek, request));
    };

    return (
        <>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 24,
                    flex: 1,
                    minHeight: 80,
                }}
            >
                <Space direction="vertical">
                    <Text strong>Week starts on: {firstDayOfWeekLabel(firstDayOfWeek)}</Text>
                    <Text type="secondary">Starting day of the week for all release calendars.</Text>
                </Space>
                <Button type="link" size="small" icon={<EditOutlined />} onClick={() => setModalOpen(true)}>
                    Edit
                </Button>
            </div>
            <Modal
                title="Edit release calendar week"
                okButtonProps={{ loading: isLoading }}
                onOk={() => form.submit()}
                okText="Save"
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                afterClose={() => {
                    form.resetFields();
                }}
            >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <Text>
                        Set the starting day of the week on the release calendar. This applies to everyone in your
                        organization.
                    </Text>
                    <Form
                        onFinish={onSave}
                        initialValues={{ calendarFirstDayOfWeek: firstDayOfWeek }}
                        style={{ marginTop: 24, width: '100%' }}
                        form={form}
                        layout="vertical"
                    >
                        <Form.Item
                            name="calendarFirstDayOfWeek"
                            label="Week starts on..."
                            style={{ width: '100%' }}
                            labelAlign={'left'}
                        >
                            <Select options={FIRST_DAY_OF_WEEK_OPTIONS} autoFocus />
                        </Form.Item>
                    </Form>
                </div>
            </Modal>
        </>
    );
};

export default OrgCalendarFirstDayOfWeekSetting;
